import { useState, useEffect, createContext, useContext } from "react";
import { Plus, Loader2, AlertCircle, X, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import ContainerNumberScanner from "./ContainerNumberScanner";
import { validateBookingForOut, CARRIERS_REQUIRING_BOOKING_ON_OUT } from "../lib/ediRequirements";
import { gateWaybillPdfBlob } from "../lib/exporters";

const GATE_TYPES = ["IN", "OUT"];

/**
 * Fire-and-forget auto-waybill. Runs after a gate event is saved successfully.
 * Sequence:
 *   1. Peek at the carrier partner to see if `waybill_email` is set. Skip if
 *      not — Arkas is the initial opt-in but every carrier can enable this by
 *      setting the field on the Partners page.
 *   2. Render the waybill PDF with the same layout the manual button uses.
 *   3. Post the bytes to `/gate-events/{id}/email-waybill?auto=true` so the
 *      audit log captures that this was auto-triggered (not manually).
 *   4. Toast the outcome — quiet on skip, success on send, warning on
 *      SMTP-not-configured, error only on real failures.
 * `photos` is the per-form photo list the operator just uploaded; we reuse
 * the local File objects instead of re-fetching from the server (faster and
 * we already have the bytes right there).
 */
export async function autoSendWaybill(saved, formPhotos) {
  try {
    const cc = (saved.carrier_code || "").toUpperCase();
    if (!cc) return;
    let partner;
    try {
      const { data } = await api.get(`/partners/${encodeURIComponent(cc)}`);
      partner = data;
    } catch {
      return; // Partner not on file — auto-send opts to fail silent.
    }
    if (!partner?.waybill_email) return;

    // Turn any in-form photos into data URLs so the PDF builder can embed them.
    const photos = [];
    for (const p of (formPhotos || []).slice(0, 3)) {
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(p.file);
        });
        photos.push({ dataUrl, caption: p.caption || "" });
      } catch { /* single-photo failure shouldn't kill the send */ }
    }

    const blob = await gateWaybillPdfBlob(saved, photos);
    const fd = new FormData();
    fd.append("auto", "true");
    fd.append("file", blob, `Waybill_${saved.container_number}_${saved.gate_type}.pdf`);
    const { data } = await api.post(`/gate-events/${saved.id}/email-waybill`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (data.skipped) {
      // Reason is either "no waybill_email configured" (we filtered above so
      // shouldn't happen) or SMTP not configured on the server.
      toast.warning(`Waybill not sent — ${data.reason || "SMTP not configured"}`);
    } else if (data.ok) {
      toast.success(`Waybill emailed to ${data.to}`);
    }
  } catch (e) {
    // Fallback: don't disrupt the operator flow; just log + soft warning.
    console.warn("auto-waybill failed", e);
    toast.warning("Waybill auto-email failed — you can resend from the Gate Activity page");
  }
}

// Prefix → carrier auto-suggest. Only the first 4 letters of the container
// number are checked. We intentionally limit this to *high-confidence*
// prefixes; lessor prefixes (TLLU, TRLU, CAIU, BSIU, TGCU, etc.) are skipped
// because their hardware is leased across multiple operators. Operator can
// always override the suggestion in the chip row below.
const PREFIX_CARRIER_MAP = {
  // Hapag-Lloyd
  GLDU: "HLC", HLBU: "HLC", HLCU: "HLC", HAMU: "HLC", HASU: "HLC", HLXU: "HLC",
  // Stolt Tank Containers (tank operator)
  UTCU: "STC", EXFU: "STC", STCU: "STC", SNTU: "STC", ICTU: "STC",
  RFLU: "STC", FLNU: "STC", SLZU: "STC",
  // ITT (chemical tank operator)
  ITTU: "ITT",
};

export function carrierFromContainerNumber(cn) {
  if (!cn || cn.length < 4) return null;
  return PREFIX_CARRIER_MAP[cn.slice(0, 4).toUpperCase()] || null;
}

// Context lets the module-level Field component read the form state without
// being re-created on every parent render — fixing the focus-loss bug where
// every keystroke unmounted the input.
const FormCtx = createContext(null);

function Field({ label, k, type = "text", required, placeholder, mono, dataListId }) {
  const ctx = useContext(FormCtx);
  if (!ctx) return null;
  const { form, update } = ctx;
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={form[k] ?? ""}
        placeholder={placeholder}
        onChange={(e) => update(k, e.target.value)}
        list={dataListId}
        autoComplete={dataListId ? "off" : undefined}
        className={`ps-input ${mono ? "font-mono" : ""}`}
        data-testid={`gate-form-${k}`}
      />
    </div>
  );
}

function emptyForm(initialDepot = null) {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
  // Location/yard defaults are depot-aware. The useEffect that fetches
  // /yards will overwrite yard_code if the user has switched depot while
  // the dialog was closed, so an operator never submits with a
  // cross-depot yard by accident.
  const dp = (initialDepot || "GH-TEM").toUpperCase();
  const LOC_DEFAULTS = {
    "GH-TEM": { yard: "TEMA", loc: "GHTEM", loc_name: "Multi Purpose Terminal Ghana (MTG)" },
    "TG-LFW": { yard: "LOME", loc: "TGLFW", loc_name: "Lom\u00e9 Depot" },
    "GA-LBV": { yard: "LBV",  loc: "GALBV", loc_name: "Libreville Depot" },
  };
  const d = LOC_DEFAULTS[dp] || LOC_DEFAULTS["GH-TEM"];
  return {
    container_number: "",
    container_iso_type: "22G1",
    gate_type: "IN",
    is_full: false,
    event_time: local,
    location_code: d.loc,
    location_name: d.loc_name,
    yard_code: d.yard,
    last_carrier: "",
    carrier_code: "",
    seal_number: "",
    bill_of_lading: "",
    booking_number: "",
    chassis_number: "",
    truck_plate: "",
    transport_ref: "",
    driver_name: "",
    customer: "",
    contact_number: "",
    grade: "B",
    condition: "SOUND",
    surveyor_name: "",
    repair_status: "NONE",
    tank_category: "",
    notes: "",
  };
}

const ISO_TILES = [
  { iso: "22G1", label: "20' DRY",    sub: "General Purpose" },
  { iso: "42G1", label: "40' DRY",    sub: "General Purpose" },
  { iso: "45G1", label: "40' HC",     sub: "High Cube" },
  { iso: "22R1", label: "20' REEFER", sub: "Refrigerated" },
  { iso: "45R1", label: "40' REEFER", sub: "Refrigerated HC" },
  { iso: "22T6", label: "20' TANK",   sub: "ISO Tank" },
  { iso: "22U1", label: "20' OPEN",   sub: "Open Top" },
  { iso: "42U1", label: "40' OPEN",   sub: "Open Top" },
  { iso: "22P1", label: "20' FLAT",   sub: "Flatrack" },
];

/** Standalone gate-event form panel. Reusable between GateEvents page header
 * "+ New" button and the global Floating Action Button. */
export function GateEventForm({ open, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [carriers, setCarriers] = useState([]);
  const [yards, setYards] = useState([]);
  const [partners, setPartners] = useState([]);
  const [intakeMatch, setIntakeMatch] = useState(null);
  const { depot } = useAuth();
  // Up to 6 photos can be staged before submit. Each: {file, caption, isDamage, preview}.
  const [photos, setPhotos] = useState([]);

  // Fetch known partners (for Customer datalist) once on mount — partner
  // master data is shared across depots, so no re-fetch on depot switch.
  useEffect(() => {
    api.get("/partners?fields=code,legal_name")
      .then((r) => setPartners(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPartners([]));
  }, []);

  // Re-fetch yards + carriers (both scoped by X-Depot) whenever the picked
  // depot changes so Togo staff see only the Lom\u00e9 yard and only the
  // carriers active at their depot.
  useEffect(() => {
    api.get("/containers/carriers")
      .then((r) => setCarriers(r.data))
      .catch(() => {});
    api.get("/yards")
      .then((r) => {
        const list = r.data || [];
        setYards(list);
        const codes = new Set(list.map((y) => y.code));
        setForm((f) => {
          if (!f || codes.has(f.yard_code)) return f;
          const def = (list.find((y) => y.default) || list[0] || {}).code || "";
          return { ...f, yard_code: def };
        });
      })
      .catch(() => {});
  }, [depot]);

  useEffect(() => {
    if (open) { setForm(emptyForm(depot)); setErr(""); setIntakeMatch(null); setPhotos([]); }
  }, [open, depot]);

  // Debounced lookup against /document-intake/by-tank to surface a pre-fill
  // banner whenever the typed container matches a known EXPECTED tank.
  useEffect(() => {
    if (!open) return;
    const cn = (form.container_number || "").toUpperCase().trim();
    if (!/^[A-Z]{3,4}\d{6,7}$/.test(cn)) { setIntakeMatch(null); return; }
    const t = setTimeout(() => {
      api.get(`/document-intake/by-tank/${cn}`)
        .then((r) => setIntakeMatch(r.data?.found ? r.data : null))
        .catch(() => setIntakeMatch(null));
    }, 300);
    return () => clearTimeout(t);
  }, [open, form.container_number]);

  const applyIntake = () => {
    if (!intakeMatch) return;
    setForm((f) => {
      // Capture both cargoes the parser knows about: previous (from the
      // cleaning certificate) and next (from the depot instruction). Wash
      // operators will fill in the 3rd-back cargo manually at wash time.
      const prev = intakeMatch.previous_cargo || "";
      const next = intakeMatch.product || "";
      let cargoLine = "";
      if (prev && next) cargoLine = `Cargoes: ${prev} → ${next}`;
      else if (prev) cargoLine = `Previous cargo: ${prev}`;
      else if (next) cargoLine = `Next load: ${next}`;
      const eftcoLine = (intakeMatch.eftco_codes || []).length
        ? `EFTCO: ${intakeMatch.eftco_codes.join(" ")}` : "";
      const poLine = intakeMatch.po_number ? `STC PO ${intakeMatch.po_number}` : "";
      const merged = [f.notes, poLine, cargoLine, eftcoLine].filter(Boolean).join(" · ");
      const firstSeal = (intakeMatch.seal_numbers || [])[0] || f.seal_number;
      return {
        ...f,
        carrier_code: "STC",
        last_carrier: "STC",
        customer: f.customer || "STOLT TANK CONTAINERS",
        container_iso_type: f.container_iso_type === "22G1" ? "22T6" : f.container_iso_type,
        tank_category: intakeMatch.tank_category || f.tank_category || "CHEM",
        seal_number: firstSeal,
        notes: merged,
      };
    });
    toast.success("Pre-filled from STC depot instruction");
    // Best-effort: mark the intake row as ARRIVED so it stops showing on next typing.
    if (intakeMatch.id) {
      api.patch(`/document-intake/${intakeMatch.id}`, { status: "ARRIVED" })
        .catch(() => {});
    }
    setIntakeMatch(null);
  };

  if (!open) return null;

  const update = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    // When the container number is set/changed and the operator hasn't picked
    // a carrier yet (or the existing carrier was auto-suggested from a
    // previous prefix), auto-fill the carrier from the prefix map. We never
    // overwrite a manual choice — once a carrier is explicitly picked from
    // the chip row, this effect stops mutating it.
    if (k === "container_number" && !f._carrier_manually_set) {
      const suggested = carrierFromContainerNumber(v);
      if (suggested) {
        next.carrier_code = suggested;
        next.last_carrier = suggested;
        next._carrier_auto_suggested = true;
      }
    }
    return next;
  });
  const pickCarrier = (code) => setForm((f) => {
    // For Stolt's three legacy codes, pre-set the tank category and unify the
    // billing carrier code to "STC" (Stolt Tank Containers B.V.) for new
    // events. The legacy codes are still valid for historical lookups.
    const isStolt = ["STC", "STOLT_F", "STOLT_C"].includes(code);
    let nextCategory = f.tank_category;
    let nextCode = code;
    if (code === "STOLT_F") { nextCategory = "FOOD"; nextCode = "STC"; }
    else if (code === "STOLT_C") { nextCategory = "CHEM"; nextCode = "STC"; }
    return {
      ...f,
      carrier_code: nextCode,
      last_carrier: nextCode,
      tank_category: isStolt ? nextCategory : f.tank_category,
      _carrier_manually_set: true,
      _carrier_auto_suggested: false,
    };
  });

  const submit = async (e, opts = {}) => {
    e.preventDefault();
    setErr("");
    if (!/^[A-Z]{3,4}\d{6,7}$/i.test(form.container_number.trim())) {
      setErr("Container number must follow ISO format, e.g. MSCU1234567");
      return;
    }
    // Carrier code is mandatory — an empty carrier means no EDI sends and
    // the tank goes ghost on carrier dashboards. Reject before the network
    // trip.
    if (!(form.carrier_code || "").trim()) {
      setErr("Carrier code is required — pick one from the quick-pick chips or type the code manually. Otherwise the EDI message won't fire.");
      return;
    }
    // Carrier-specific pre-flight: ARKAS rejects gate-OUT EDI without a
    // booking / service-order reference. Block the save here.
    if (form.gate_type === "OUT") {
      const carrier = (form.last_carrier || form.carrier_code || "").toUpperCase();
      const bookingErr = validateBookingForOut(carrier, form.booking_number);
      if (bookingErr) { setErr(bookingErr); return; }
    }
    if (submitting) return;     // hard re-entrancy guard
    setSubmitting(true);
    try {
      const { _carrier_manually_set: _m, _carrier_auto_suggested: _a, ...formForApi } = form;
      const payload = {
        ...formForApi,
        container_number: form.container_number.toUpperCase().trim(),
        event_time: new Date(form.event_time).toISOString(),
        tank_category: form.tank_category || null,
        // Per save-click idempotency key — server dedupes if the same key
        // arrives twice (double-click, network retry, etc.).
        idempotency_key: crypto.randomUUID(),
      };
      const { data } = await api.post("/gate-events", payload);

      // Photos: upload each one after the gate event is persisted. We swallow
      // individual upload failures so a flaky photo doesn't lose the event
      // itself — the toast surfaces partial success.
      let photoOk = 0, photoFail = 0;
      for (const p of photos) {
        try {
          const fd = new FormData();
          fd.append("file", p.file);
          fd.append("caption", p.caption || "");
          fd.append("is_damage", p.isDamage ? "true" : "false");
          await api.post(`/gate-events/${data.id}/photos`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          photoOk++;
        } catch (pe) {
          photoFail++;
          console.error("photo upload failed", pe);
        }
      }
      if (photos.length) {
        if (photoFail === 0) toast.success(`${photoOk} photo${photoOk === 1 ? "" : "s"} attached`);
        else toast.error(`${photoFail} of ${photos.length} photo upload(s) failed`);
      }

      const auto = data._auto_edi;
      if (auto?.status === "SENT") {
        toast.success(`Gate ${data.gate_type} recorded · EDI sent automatically`);
      } else if (auto?.status === "FAILED") {
        toast.error(`Gate ${data.gate_type} recorded but EDI failed: ${(auto.last_error || "").slice(0, 80)}`);
      } else {
        toast.success(`Gate ${data.gate_type} recorded for ${data.container_number}`);
      }
      // Surface auto-offhire so the operator knows the rental clock stopped.
      if (data._auto_offhire) {
        const o = data._auto_offhire;
        toast.success(
          `${o.customer_code} rental closed — ${o.tank_number} dropped off ${o.drop_off_date}`,
          { duration: 6000 },
        );
      }

      // Auto-email e-waybill — carrier partners opt in by setting `waybill_email`
      // on their partner record. Fire-and-forget so a slow SMTP hop doesn't
      // block the operator from processing the next truck. Failure only shows
      // a warning toast; the manual "Waybill" button remains as fallback.
      autoSendWaybill(data, photos).catch(() => { /* logged in helper */ });
      onSaved?.(data);
      if (opts.keepOpen) {
        // Reset only the per-truck fields. Keep the carrier, gate type, depot
        // location and tank category since a convoy usually shares those.
        setForm((f) => ({
          ...f,
          container_number: "",
          container_iso_type: f.container_iso_type,
          is_full: f.is_full,
          customer: "",
          contact_number: "",
          truck_plate: "",
          transport_ref: "",
          driver_name: "",
          surveyor_name: "",
          seal_number: "",
          bill_of_lading: "",
          booking_number: "",
          chassis_number: "",
          notes: "",
          event_time: emptyForm().event_time,
        }));
        setPhotos([]);
        // Move focus straight back to the container number field for the next unit
        setTimeout(() => {
          document.querySelector("[data-testid='gate-form-container_number']")?.focus();
        }, 50);
      } else {
        onClose();
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormCtx.Provider value={{ form, update }}>
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-stretch justify-end" data-testid="gate-event-form-modal">
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto border-l border-gray-200">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">New record</div>
            <h2 className="font-display text-xl font-extrabold tracking-tight">Gate event</h2>
          </div>
          <button onClick={onClose} className="ps-btn ps-btn-secondary" data-testid="close-gate-form">
            <X className="w-4 h-4" /> Close
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-6">
          <datalist id="gate-customer-partners">
            {partners.map((p) => (
              <option key={p.code} value={p.legal_name || p.code} label={p.code} />
            ))}
          </datalist>
          {err && (
            <div className="flex items-start gap-2 border border-red-200 bg-red-50 px-3 py-2 rounded-sm text-sm text-red-700" data-testid="gate-form-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          {intakeMatch && (
            <div
              className="flex items-start gap-3 border border-emerald-300 bg-emerald-50 px-3 py-3 rounded-sm"
              data-testid="intake-prefill-banner"
            >
              <Sparkles className="w-5 h-5 mt-0.5 shrink-0 text-emerald-700" />
              <div className="flex-1 text-xs text-emerald-900 space-y-1">
                <div className="font-bold uppercase tracking-[0.14em] text-[10px] text-emerald-700">
                  Expected from STC · PO {intakeMatch.po_number || "—"}
                </div>
                <div className="font-mono">
                  Tank <b>{intakeMatch.tank_number}</b>
                  {intakeMatch.previous_cargo ? <> · prev <b>{intakeMatch.previous_cargo}</b></> : null}
                  {intakeMatch.product ? <> · next load <b>{intakeMatch.product}</b></> : null}
                </div>
                {(intakeMatch.eftco_codes || []).length > 0 && (
                  <div className="font-mono text-[10px] text-emerald-800">
                    EFTCO: {intakeMatch.eftco_codes.join(" ")}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={applyIntake}
                className="ps-btn ps-btn-primary"
                data-testid="apply-intake-prefill"
              >
                <Sparkles className="w-4 h-4" /> Apply
              </button>
            </div>
          )}

          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">Container</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                  Container number <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text" required value={form.container_number}
                    placeholder="MSCU1234567"
                    onChange={(e) => update("container_number", e.target.value.toUpperCase())}
                    className="ps-input font-mono flex-1"
                    data-testid="gate-form-container_number"
                  />
                  <ContainerNumberScanner dataTestid="gate-ocr" onResult={(cn) => update("container_number", cn)} />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                  Container type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2" data-testid="iso-type-picker">
                  {ISO_TILES.map((t) => (
                    <button
                      type="button" key={t.iso}
                      onClick={() => update("container_iso_type", t.iso)}
                      data-testid={`iso-type-${t.iso}`}
                      className={`px-2 py-2 rounded-sm border text-left transition-colors ${
                        form.container_iso_type === t.iso
                          ? "bg-[#006E9E] text-white border-[#006E9E]"
                          : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="text-xs font-bold leading-tight">{t.label}</div>
                      <div className={`text-[9px] font-mono leading-tight mt-0.5 ${form.container_iso_type === t.iso ? "opacity-80" : "text-gray-500"}`}>{t.iso} · {t.sub}</div>
                    </button>
                  ))}
                </div>
                <details className="mt-1.5">
                  <summary className="text-[10px] text-gray-500 cursor-pointer hover:text-gray-700">Other ISO code…</summary>
                  <input type="text" value={form.container_iso_type}
                    onChange={(e) => update("container_iso_type", e.target.value.toUpperCase())}
                    className="ps-input font-mono mt-1.5" placeholder="e.g. L5G1"
                    data-testid="iso-type-custom" />
                </details>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                  Gate type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {GATE_TYPES.map((g) => (
                    <button
                      type="button" key={g} onClick={() => update("gate_type", g)}
                      data-testid={`gate-type-${g}`}
                      className={`h-9 rounded-sm border text-sm font-semibold transition-colors ${
                        form.gate_type === g
                          ? "bg-[#006E9E] text-white border-[#006E9E]"
                          : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      {g === "IN" ? "Gate IN" : "Gate OUT"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Load status</label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => update("is_full", true)} data-testid="load-full"
                    className={`h-9 rounded-sm border text-sm font-semibold transition-colors ${form.is_full ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}>Full</button>
                  <button type="button" onClick={() => update("is_full", false)} data-testid="load-empty"
                    className={`h-9 rounded-sm border text-sm font-semibold transition-colors ${!form.is_full ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}>Empty</button>
                </div>
              </div>
            </div>
          </section>

          <section data-testid="carrier-section">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 flex items-center gap-2">
                Carrier
                {form._carrier_auto_suggested && form.carrier_code && (
                  <span
                    className="normal-case tracking-normal text-[10px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 rounded-sm"
                    data-testid="carrier-auto-suggested"
                    title={`Auto-suggested from container prefix ${form.container_number?.slice(0, 4)}. Click another carrier to override.`}
                  >
                    Auto: {form.container_number?.slice(0, 4)} → {form.carrier_code}
                  </span>
                )}
              </div>
              {form.carrier_code && (
                <button
                  type="button"
                  onClick={() => pickCarrier("")}
                  className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-400 hover:text-red-600"
                  data-testid="carrier-clear"
                >Clear</button>
              )}
            </div>
            {carriers.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="carrier-quickpick">
                {carriers.map((c) => {
                  const active = form.carrier_code === c.carrier;
                  return (
                    <button
                      key={c.carrier}
                      type="button"
                      onClick={() => pickCarrier(c.carrier)}
                      className={`text-[11px] font-mono px-2.5 py-1.5 border rounded-sm transition ${
                        active
                          ? "bg-[#006E9E] text-white border-[#006E9E]"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-[#006E9E]/10 hover:border-[#006E9E]/60"
                      }`}
                      data-testid={`carrier-chip-${c.carrier}`}
                    >
                      {c.carrier}
                      <span className={`ml-1.5 text-[10px] ${active ? "opacity-80" : "text-gray-400"}`}>{c.count}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-[11px] text-gray-400 mb-3">No carriers seen yet — type one below.</div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Carrier code" k="carrier_code" required mono placeholder="Pick chip or type code…" dataListId="gate-carrier-codes" />
              <Field label="Display name (optional)" k="last_carrier" mono placeholder="auto-fills from chip" />
            </div>
            <datalist id="gate-carrier-codes">
              {partners.map((p) => (
                <option key={p.code} value={p.code} label={p.legal_name} />
              ))}
            </datalist>

            {/* Tank category — Food vs Chem. Shown for ISO tanks; auto-set
                when the operator picks STOLT_F or STOLT_C from chips. */}
            {(form.container_iso_type?.includes("T") || form.tank_category) && (
              <div className="mt-4" data-testid="tank-category-section">
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                  Tank grade <span className="font-normal normal-case text-gray-400">(billing & stock report)</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button type="button" onClick={() => update("tank_category", "")}
                    data-testid="tank-category-none"
                    className={`h-9 rounded-sm border text-xs font-semibold transition-colors ${
                      !form.tank_category ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                    }`}>Not applicable</button>
                  <button type="button" onClick={() => update("tank_category", "FOOD")}
                    data-testid="tank-category-FOOD"
                    className={`h-9 rounded-sm border text-xs font-bold transition-colors ${
                      form.tank_category === "FOOD"
                        ? "bg-[#99D420] text-[#1f3500] border-[#7eb010]"
                        : "bg-white text-[#3a6500] border-[#99D420]/40 hover:bg-[#99D420]/15"
                    }`}>FOOD GRADE</button>
                  <button type="button" onClick={() => update("tank_category", "CHEM")}
                    data-testid="tank-category-CHEM"
                    className={`h-9 rounded-sm border text-xs font-bold transition-colors ${
                      form.tank_category === "CHEM"
                        ? "bg-[#F1471D] text-white border-[#c0360e]"
                        : "bg-white text-[#9b2a0f] border-[#F1471D]/40 hover:bg-[#F1471D]/15"
                    }`}>CHEMICAL</button>
                </div>
              </div>
            )}
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">Event</div>

            {/* Prominent yard picker — color-coded card buttons so a busy gate
                clerk can't pick the wrong yard by accident. Required for every
                gate IN/OUT to keep stock counts split correctly. */}
            <div className="mb-4">
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-2">
                Yard <span className="text-red-500">*</span>
                <span className="ml-2 normal-case font-normal text-[10px] text-gray-500">
                  Pick where this tank physically is. Depot-specific — only yards for the currently selected depot are shown.
                </span>
              </label>
              <div
                className={`grid gap-3 ${yards.length <= 1 ? "grid-cols-1" : "grid-cols-2"}`}
                role="radiogroup"
                aria-label="Yard selection"
              >
                {yards.length === 0 && (
                  <div className="rounded-md border-2 border-dashed border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="gate-form-yard-empty">
                    No yards configured for this depot. Ask an admin to add at least one yard before you can log gate events.
                  </div>
                )}
                {yards.map((y, i) => {
                  const selected = form.yard_code === y.code;
                  const isMain = i === 0 || y.default;
                  return (
                    <button
                      key={y.code}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => update("yard_code", y.code)}
                      data-testid={`gate-form-yard-${y.code}`}
                      className={
                        "relative text-left rounded-md border-2 px-4 py-3 transition-all " +
                        (selected
                          ? (isMain
                              ? "border-[#002FA7] bg-[#002FA7]/5 shadow-sm"
                              : "border-[#F1471D] bg-[#F1471D]/5 shadow-sm")
                          : "border-gray-200 bg-white hover:border-gray-300")
                      }
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={
                            "inline-block w-3 h-3 rounded-full " +
                            (selected
                              ? (isMain ? "bg-[#002FA7]" : "bg-[#F1471D]")
                              : "bg-gray-300")
                          }
                        />
                        <span
                          className={
                            "font-semibold text-sm " +
                            (selected
                              ? (isMain ? "text-[#002FA7]" : "text-[#F1471D]")
                              : "text-gray-700")
                          }
                        >
                          {y.name}
                        </span>
                        {typeof y.in_depot === "number" && (
                          <span className="ml-auto text-[10px] font-mono text-gray-500">
                            {y.in_depot} in stock
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-[10px] uppercase tracking-[0.14em] text-gray-500 font-mono">
                        {y.code}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Event time" k="event_time" type="datetime-local" required />
              <Field label="Location code" k="location_code" required mono />
              <Field label="Location name" k="location_name" />
              <Field label={form.gate_type === "OUT" ? "Consignee (required for gate OUT)" : "Customer / Consignee"} k="customer" required={form.gate_type === "OUT"} dataListId="gate-customer-partners" />
              <Field label="Contact number" k="contact_number" mono />
              <Field label="Truck plate" k="truck_plate" mono />
              <Field label="Transport reference (RFF+TN on EDI)" k="transport_ref" mono />
              <Field label="Driver name" k="driver_name" />
            </div>
            {form.gate_type === "OUT" && (
              <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm">
                Gate OUT requires Consignee, Truck plate and Driver name. Contact number is optional. If the carrier has an EDI recipient mapping, CODECO95B will be sent automatically.
              </div>
            )}
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">Empty depot condition</div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Grade</label>
                <div className="grid grid-cols-3 gap-1">
                  {["A", "B", "C"].map((g) => (
                    <button key={g} type="button" onClick={() => update("grade", g)} data-testid={`grade-${g}`}
                      className={`h-9 rounded-sm border text-sm font-bold transition-colors ${form.grade === g ? "bg-[#006E9E] text-white border-[#006E9E]" : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"}`}>{g}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Condition</label>
                <select value={form.condition} onChange={(e) => update("condition", e.target.value)} className="ps-input" data-testid="gate-form-condition">
                  <option value="SOUND">Sound</option>
                  <option value="DAMAGED">Damaged</option>
                  <option value="UNDER_SURVEY">Under survey</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Repair status</label>
                <select value={form.repair_status} onChange={(e) => update("repair_status", e.target.value)} className="ps-input" data-testid="gate-form-repair-status">
                  <option value="NONE">None</option>
                  <option value="QUOTED">Quoted</option>
                  <option value="IN_REPAIR">In repair</option>
                  <option value="REPAIRED">Repaired</option>
                </select>
              </div>
              <Field label="Surveyor" k="surveyor_name" />
              <Field label="Seal number" k="seal_number" mono />
            </div>
          </section>

          <section>
            {(() => {
              const cc = (form.last_carrier || form.carrier_code || "").toUpperCase();
              const bookingRequired = form.gate_type === "OUT" && CARRIERS_REQUIRING_BOOKING_ON_OUT.has(cc);
              return (
                <>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">
                    {bookingRequired ? `References (booking required for ${cc} gate-OUT EDI)` : "References (optional)"}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field
                      label={bookingRequired ? "Booking / Service-Order # *" : "Booking number"}
                      k="booking_number"
                      mono
                      required={bookingRequired}
                      placeholder={bookingRequired ? `Required by ${cc}` : "e.g. HLCUBKG12345"}
                    />
                    <Field label="Bill of lading" k="bill_of_lading" mono />
                    <Field label="Chassis number" k="chassis_number" mono />
                  </div>
                  {bookingRequired && !form.booking_number && (
                    <div className="text-[11px] text-[#F1471D] mt-2 px-1" data-testid="booking-warning">
                      {cc} rejects gate-OUT EDI without a booking / service-order reference.
                    </div>
                  )}
                </>
              );
            })()}
          </section>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Notes</label>
            <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={3} className="ps-input" data-testid="gate-form-notes" />
          </div>

          {/* Back-date evidence nudge — non-blocking. Triggered when the event
              is >7 days in the past AND no booking number / release order /
              notes have been supplied. Pattern caused by the PTIT/Trident
              mix-up where 5 boxes were entered a month late with no paper. */}
          {(() => {
            if (!form.event_time) return null;
            const days = (Date.now() - new Date(form.event_time).getTime()) / 86400000;
            const hasEvidence = (form.booking_number || form.release_order_id || form.notes || "").trim().length > 0;
            if (days <= 7 || hasEvidence) return null;
            return (
              <div
                className="bg-amber-50 border border-amber-300 rounded-md px-4 py-3 text-[12px] text-amber-900 flex gap-3"
                data-testid="backdate-warning"
              >
                <span className="text-amber-600 text-base leading-none">⚠</span>
                <div>
                  <div className="font-semibold mb-0.5">
                    Back-dated by {Math.round(days)} days — please add a paper trail
                  </div>
                  <div className="text-amber-800">
                    Add a <strong>booking number</strong>, <strong>release order</strong>, or a short
                    <strong> note</strong> explaining the delay. The event will still save without it,
                    but undocumented back-dates lead to billing disputes later.
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Photos (max 6) ───────────────────────────────────────────── */}
          <section data-testid="gate-form-photos-section">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-2 flex items-center justify-between">
              <span>Photos</span>
              <span className="text-gray-400 normal-case tracking-normal text-[11px] font-normal">
                {photos.length} / 6 · attach evidence or damage shots
              </span>
            </div>
            {photos.length < 6 && (
              <label
                className="block border-2 border-dashed border-gray-300 hover:border-[#002FA7] hover:bg-blue-50/30 rounded-md px-4 py-3 cursor-pointer text-center text-sm text-gray-600 transition-colors mb-3"
                data-testid="gate-form-photo-dropzone"
              >
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
                  multiple
                  capture="environment"
                  className="hidden"
                  onChange={(ev) => {
                    const incoming = Array.from(ev.target.files || []);
                    const room = 6 - photos.length;
                    const slice = incoming.slice(0, room);
                    const fresh = slice.map((file) => ({
                      file,
                      caption: "",
                      isDamage: false,
                      preview: URL.createObjectURL(file),
                      key: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
                    }));
                    setPhotos((prev) => [...prev, ...fresh]);
                    ev.target.value = "";
                  }}
                  data-testid="gate-form-photo-input"
                />
                <span className="font-semibold">Tap to add photos</span>
                <span className="block text-[11px] text-gray-500 mt-0.5">
                  Front, back, sides, roof, base. Mark damage shots with the red checkbox.
                </span>
              </label>
            )}
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-3">
                {photos.map((p, idx) => (
                  <div
                    key={p.key}
                    className={
                      "relative rounded-md border-2 p-2 bg-white " +
                      (p.isDamage ? "border-red-500" : "border-gray-200")
                    }
                    data-testid={`gate-form-photo-card-${idx}`}
                  >
                    <img
                      src={p.preview}
                      alt={`photo ${idx + 1}`}
                      className="w-full h-24 object-cover rounded-sm bg-gray-100"
                    />
                    {p.isDamage && (
                      <span
                        className="absolute top-1 left-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider"
                      >
                        Damage
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setPhotos((prev) => prev.filter((x) => x.key !== p.key))}
                      className="absolute top-1 right-1 bg-white/90 hover:bg-white border border-gray-300 w-5 h-5 rounded-full text-[10px] text-gray-700 leading-none flex items-center justify-center"
                      title="Remove"
                      data-testid={`gate-form-photo-remove-${idx}`}
                    >
                      ×
                    </button>
                    <input
                      type="text"
                      value={p.caption}
                      maxLength={120}
                      onChange={(ev) => {
                        const v = ev.target.value;
                        setPhotos((prev) =>
                          prev.map((x) => (x.key === p.key ? { ...x, caption: v } : x))
                        );
                      }}
                      placeholder="Caption (optional)"
                      className="ps-input mt-2 text-xs py-1.5"
                      data-testid={`gate-form-photo-caption-${idx}`}
                    />
                    <label className="flex items-center gap-1.5 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={p.isDamage}
                        onChange={(ev) => {
                          const v = ev.target.checked;
                          setPhotos((prev) =>
                            prev.map((x) => (x.key === p.key ? { ...x, isDamage: v } : x))
                          );
                        }}
                        className="w-3.5 h-3.5 accent-red-600"
                        data-testid={`gate-form-photo-damage-${idx}`}
                      />
                      <span className="text-[11px] text-gray-700 select-none">This shows damage</span>
                    </label>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="sticky bottom-0 bg-white border-t border-gray-200 -mx-6 px-6 py-4 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="ps-btn ps-btn-secondary" data-testid="cancel-gate-form">Cancel</button>
            <button
              type="button"
              disabled={submitting}
              onClick={(e) => submit(e, { keepOpen: true })}
              className="ps-btn ps-btn-secondary"
              data-testid="submit-gate-form-add-another"
              title="Save this gate event and clear the truck details so you can record the next unit in the convoy"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Plus className="w-4 h-4" /> Save &amp; add another</>}
            </button>
            <button type="submit" disabled={submitting} className="ps-btn ps-btn-primary" data-testid="submit-gate-form">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save event"}
            </button>
          </div>
        </form>
      </div>
    </div>
    </FormCtx.Provider>
  );
}


// Roles allowed to create gate events (mirror backend ROLES_GATE_CREATE)
const ROLES_CAN_GATE = new Set(["admin", "supervisor", "depot_manager", "operator"]);

/** Floating Action Button — always-on bottom-right gate-event quick-record.
 * Hidden on /login. Only renders for roles that can create gate events. */
export default function GateEventFab() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  // Hide on login page
  if (typeof window !== "undefined" && window.location.pathname === "/login") return null;
  if (!user || !ROLES_CAN_GATE.has(user.role)) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Record gate event"
        data-testid="gate-event-fab"
        className="fixed bottom-20 right-6 z-40 h-14 w-14 rounded-full bg-[#006E9E] text-white shadow-lg shadow-[#006E9E]/30 hover:bg-[#003147] hover:shadow-xl hover:shadow-[#003147]/40 transition-all duration-200 flex items-center justify-center group focus:outline-none focus:ring-4 focus:ring-[#006E9E]/30"
      >
        <Plus className="w-6 h-6 transition-transform duration-200 group-hover:rotate-90" />
        <span className="sr-only">New gate event</span>
        {/* Hover label */}
        <span className="absolute right-full mr-3 whitespace-nowrap rounded-md bg-[#003147] px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 pointer-events-none">
          New gate event
        </span>
      </button>
      <GateEventForm
        open={open}
        onClose={() => setOpen(false)}
        onSaved={() => {
          // Refresh /gate-events page if currently visible
          if (window.location.pathname === "/gate-events") {
            window.dispatchEvent(new CustomEvent("gate-events:refresh"));
          }
        }}
      />
    </>
  );
}
